import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import NoteIcon from "@mui/icons-material/Note";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Tab,
	Tabs,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import type { MessageDocument } from "../../api/documents/message-documents";
import type { PersonEmail } from "../../api/personemail/person-email";
import { MessageDocumentsField } from "../messaging/message-documents-field";
import { ActivityPanel } from "./activity-add-dialog/activity-panel";
import { EmailPanel } from "./activity-add-dialog/email-panel";
import { LinkedInPanel } from "./activity-add-dialog/linkedin-panel";
import { NotePanel } from "./activity-add-dialog/note-panel";
import type { ActivityPanelHandle } from "./activity-add-dialog/panel-types";

type ActivityMode = "note" | "call" | "meeting" | "email" | "linkedin";

interface ActivityAddDialogProps {
	open: boolean;
	onClose: () => void;
	personId: number;
	userId: number;
	onSuccess: () => void;
	initialMode?: ActivityMode;
	personEmails?: PersonEmail[];
	isFirstDegree?: boolean;
	/** Scopes the document picker to this assignment and its company. */
	assignmentId?: number | null;
}

export const ActivityAddDialog = ({
	open,
	onClose,
	personId,
	userId,
	onSuccess,
	initialMode = "note",
	personEmails = [],
	isFirstDegree = false,
	assignmentId = null,
}: ActivityAddDialogProps) => {
	const [mode, setMode] = useState<ActivityMode>(initialMode);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [canSave, setCanSave] = useState(false);
	// The dialog owns the selection so the attach control can sit below the
	// compose fields, where the recruiter is already looking, rather than being
	// buried inside each channel's panel.
	const [documents, setDocuments] = useState<MessageDocument[]>([]);

	const panelRef = useRef<ActivityPanelHandle>(null);

	const isMessageMode = mode === "email" || mode === "linkedin";

	const handleCanSaveChange = useCallback((value: boolean) => {
		setCanSave(value);
	}, []);

	const handleModeChange = (_: unknown, newMode: ActivityMode) => {
		setMode(newMode);
		setCanSave(false);
		// A note or a call carries no documents, so switching away drops them
		// rather than leaving an invisible selection to reappear later.
		if (newMode !== "email" && newMode !== "linkedin") setDocuments([]);
	};

	const handleSave = async () => {
		if (!panelRef.current) return;
		setSaving(true);
		setError(null);
		try {
			await panelRef.current.save();
			onSuccess();
			handleClose();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to save activity";
			setError(message);
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		setError(null);
		setCanSave(false);
		setDocuments([]);
		onClose();
	};

	const dialogTitle = isMessageMode ? "Send Message" : "Add Activity";
	const saveButtonText = saving
		? isMessageMode
			? "Sending..."
			: "Saving..."
		: isMessageMode
			? "Send"
			: "Save";

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			data-component="activity-add-dialog"
		>
			<DialogTitle data-component="activity-add-title">{dialogTitle}</DialogTitle>
			<DialogContent
				data-component="activity-add-content"
				sx={{ minHeight: 380 }}
			>
				<Tabs
					value={mode}
					onChange={handleModeChange}
					sx={{ mb: 2 }}
					variant="scrollable"
					scrollButtons="auto"
					data-component="activity-mode-tabs"
				>
					<Tab
						icon={<NoteIcon />}
						iconPosition="start"
						label="Note"
						value="note"
						data-component="tab-note"
					/>
					<Tab
						icon={<PhoneIcon />}
						iconPosition="start"
						label="Call"
						value="call"
						data-component="tab-call"
					/>
					<Tab
						icon={<VideocamIcon />}
						iconPosition="start"
						label="Meeting"
						value="meeting"
						data-component="tab-meeting"
					/>
					<Tab
						icon={<EmailIcon />}
						iconPosition="start"
						label="Email"
						value="email"
						data-component="tab-email"
					/>
					<Tab
						icon={<LinkedInIcon />}
						iconPosition="start"
						label="LinkedIn"
						value="linkedin"
						data-component="tab-linkedin"
					/>
				</Tabs>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }} data-component="error-alert">
						{error}
					</Alert>
				)}

				{mode === "note" && (
					<NotePanel
						ref={panelRef}
						personId={personId}
						onCanSaveChange={handleCanSaveChange}
					/>
				)}
				{(mode === "call" || mode === "meeting") && (
					<ActivityPanel
						ref={panelRef}
						personId={personId}
						mode={mode}
						onCanSaveChange={handleCanSaveChange}
					/>
				)}
				{mode === "email" && (
					<EmailPanel
						ref={panelRef}
						personId={personId}
						userId={userId}
						personEmails={personEmails}
						documents={documents}
						onCanSaveChange={handleCanSaveChange}
					/>
				)}
				{mode === "linkedin" && (
					<LinkedInPanel
						ref={panelRef}
						personId={personId}
						userId={userId}
						isFirstDegree={isFirstDegree}
						documents={documents}
						onCanSaveChange={handleCanSaveChange}
					/>
				)}
				{isMessageMode && (
					<>
						<Divider sx={{ my: 2 }} data-component="documents-divider" />
						<MessageDocumentsField
							value={documents}
							onChange={setDocuments}
							assignmentId={assignmentId}
							disabled={saving}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions data-component="activity-add-actions">
				<Button onClick={handleClose} data-component="cancel-button">
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={saving || !canSave}
					data-component="save-button"
				>
					{saveButtonText}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ActivityAddDialog;
